import { useMemo } from "react";
import type { TicketPriority } from "@/api/tickets";
import { useRequest } from "@/request";
import type { PagedResultDto } from "@/types/abp";

/** 工单分类（对应 Api.Admin CategoryDto，JSON 为 camelCase）。 */
export type CategoryDto = {
  id: string;
  /** NULL 表示跨项目通用分类。 */
  projectId: string | null;
  parentId: string | null;
  name: string;
  /** 由后端按父子层级维护的路径，如 `父/子`。 */
  path: string;
  /** NULL 表示继承最近已配置的上级；没有上级配置时使用全局普通。 */
  defaultPriority: TicketPriority | null;
  /** 未配置时继承最近上级分类的建单表单。 */
  intakeFormDefinitionId?: string | null;
  concurrencyStamp: string;
};

export type CategoryListRequest = {
  projectId?: string | null;
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
};

export type CreateCategoryInput = {
  name: string;
  projectId?: string | null;
  parentId?: string | null;
  defaultPriority?: TicketPriority | null;
  intakeFormDefinitionId?: string | null;
};

export type UpdateCategoryInput = {
  name: string;
  projectId?: string | null;
  parentId?: string | null;
  defaultPriority?: TicketPriority | null;
  intakeFormDefinitionId?: string | null;
  concurrencyStamp: string;
};

function buildQuery(request: CategoryListRequest): string {
  const params = new URLSearchParams();
  if (request.projectId) {
    params.set("ProjectId", request.projectId);
  }
  if (request.skipCount !== undefined) {
    params.set("SkipCount", String(request.skipCount));
  }
  if (request.maxResultCount !== undefined) {
    params.set("MaxResultCount", String(request.maxResultCount));
  }
  if (request.sorting) {
    params.set("Sorting", request.sorting);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** 工单分类 CRUD 客户端（挂在 Api.Admin `categories` 端点上）。 */
export function useCategoryApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      getList: (input: CategoryListRequest = {}, signal?: AbortSignal) =>
        request<PagedResultDto<CategoryDto>>(`categories${buildQuery(input)}`, { method: "GET", signal, skipErrorToast: true }),
      create: (input: CreateCategoryInput) => request<CategoryDto>("categories", { method: "POST", data: input }),
      update: (id: string, input: UpdateCategoryInput) => request<CategoryDto>(`categories/${id}`, { method: "PUT", data: input }),
      remove: (id: string) => request<void>(`categories/${id}`, { method: "DELETE" }),
    }),
    [request],
  );
}
